<template>
    <div class="roster-sticky-header surface-0 mb-2" :class="{ 'leaves-sticky-header': props.viewType === 'leaves' }">
        <div class="roster-header-summary flex align-items-center">
          <div class="px-0 py-2">
            <h2 class="m-0 p-0 font-light text-600">{{ pageTitle }}</h2>
            <p v-if="selectedRows.length" class="selection-summary p-0 m-0 pt-1 text-xs text-500 font-semibold">
              {{ selectionSummary }}
            </p>
            <p v-else class="p-0 m-0 pt-1 text-xs text-300">
              Showing {{ allRosters.length }} of {{ rosterPagination.total_items }} results.
            </p>
          </div>
        </div>
        <div class="roster-filters">
          <div style="position: fixed; right: 30px; bottom: 30px; z-index: 50;" v-if="selectedRows.length > 0" class="shadow-8 m-3">
            <Button v-if="canMarkSelectedRoster" @click="openAttendanceMarker" label="Mark Attendance" icon="pi pi-clock" class="border-0 text-lg bg-green-700 hover:bg-green-500 mr-2 w-full mb-2" />
            <Button v-if="canEditSelectedAttendance" @click="openAttendanceEditor" label="Edit Attendance" icon="pi pi-pencil" class="border-0 text-lg bg-cyan-700 hover:bg-cyan-500 mr-2 w-full mb-2" />
            <Button v-if="selectedSingleRosterId" @click="viewLogs=true" label="View Logs" class="border-0 text-lg surface-300 hover:surface-500 mr-2 w-full mb-2" />
            <Button @click="openRosterEditor" :label="`Edit ${props.viewType}`" class="px-4 py-3 text-lg w-full block shadow-3 surface-300 hover:surface-500 w-full" />
          </div>
          <Select v-if="!isDropdownDisabled"
                  v-model="selectedCompanyId"
                  :options="companiesList"
                  optionLabel="company_name"
                  optionValue="company_id"
                  filter
                  showClear
                  size="small"
                  filterPlaceholder="Search companies"
                  :loading="selectionLoading"
                  :disabled="isRefreshing || selectionLoading"
                  placeholder="All Companies"
                  class="w-full md:w-13rem"
                  @change="handleCompanyChange" />
          <MultiSelect v-if="!isDropdownDisabled"
                      v-model="companyEmployeeIds"
                      :options="employeeOptions"
                      optionLabel="employee_name"
                      optionValue="employee_id"
                      size="small"
                      filter
                      filterPlaceholder="Search employees"
                      :loading="selectionLoading"
                      :disabled="isRefreshing || selectionLoading"
                      placeholder="All Employees"
                      class="w-full md:w-18rem">
            <template #value>
              <span>{{ employeeSelectionLabel }}</span>
            </template>
          </MultiSelect>
          <Calendar v-model="dateRange" 
                    selectionMode="range" 
                    :showIcon="true" 
                    :manualInput="false"
                    :disabled="isRefreshing"
                    dateFormat="yy-mm-dd"
                    placeholder="Select Date Range" 
                    class="w-full md:w-15rem"
                    size="small"
                    :hideOnRangeSelection="true"
                    @date-select="handleDateSelect" />
          <Dropdown v-if="props.viewType === 'roster'" v-model="selectedType" :options="typeList" :disabled="isRefreshing" optionLabel="name" placeholder="Type" class="w-full md:w-8rem" size="small">
            <template #value="slotProps">
                <div v-if="slotProps.value" class="flex align-items-center">
                    {{ slotProps.value.name }}
                </div>
                <span v-else class="text-sm text-300">
                    {{ slotProps.placeholder }}
                </span>
            </template>
          </Dropdown>
          <div v-if="props.viewType === 'roster'" class="marked-filter flex align-items-center gap-3" role="radiogroup" aria-label="Roster marked status">
            <label v-for="option in markedStatusList" :key="option.code" :for="`marked-${option.code.toLowerCase()}`" class="flex align-items-center gap-1 cursor-pointer white-space-nowrap">
              <RadioButton v-model="selectedMarkedStatus" :inputId="`marked-${option.code.toLowerCase()}`" name="roster-marked-status" :value="option.code" :disabled="isRefreshing" />
              <span>{{ option.name }}</span>
            </label>
          </div>
          <Button v-if="!isDropdownDisabled"
                  icon="pi pi-search"
                  label="Apply"
                  aria-label="Apply roster filters"
                  v-tooltip.top="'Apply filters'"
                  :loading="isRefreshing"
                  :disabled="!hasCompleteDateRange || selectionLoading"
                  @click="handleApplyClick" />
          <div class="card flex justify-content-center" v-if="selectedEmployeeCompanyId === 1">
              <Button icon="pi pi-plus" :disabled="!selectedEmployeeId || isRefreshing || props.viewType === 'leaves'" @click="toggle"  aria-haspopup="true" aria-controls="overlay_menu" class="surface-300 hover:bg-primary-600 mr-2" />
              <Menu ref="menu" id="overlay_menu" :model="menuItems" :popup="true" />
          </div>
          <div class="card flex justify-content-center" v-else>
              <Button icon="pi pi-plus" :disabled="!selectedEmployeeId || isRefreshing || props.viewType === 'leaves'" @click="createRows(1, 'SHIFT')" class="surface-300 hover:bg-primary-600" />
          </div>
      </div>
    </div>
    <div v-if="rosterLoading || rosterPagination.total_items">
      <DataTable :value="allRosters"
                 selectionMode="multiple"
                 v-model:selection="selectedRows"
                 lazy
                 tableStyle="min-height: 20rem"
                 sortMode="single"
                 :sortField="sortField"
                 :sortOrder="sortOrder"
                 :loading="rosterLoading || isRefreshing"
                 @sort="onSort">
        <Column selectionMode="multiple" style="width: 1rem;"></Column>
        <Column field="employee_name" header="Employee" class="text-600 w-3">
          <template #body="slotProps">
              <div class="flex items-center gap-2 align-content-center">
                  <img alt="flag" :src="slotProps.data.employee_photo_url || defaultUserImage" class="max-w-3rem max-h-3rem employee-photo	" />
                  <div>
                    <span class="block font-bold">{{ slotProps.data.employee_name }}</span>
                    <span class="block text-xs text-300">{{ slotProps.data.employee_company_name }}</span>
                    <span class="text-xs text-300 mr-2">
                      <span class="mr-2">@{{ slotProps.data.employee_id }}</span>
                      <span class="mr-2">{{ slotProps.data.employee_status }}</span>
                      <span class="mr-2">{{ slotProps.data.employee_department }}</span>
                    </span>
                  </div>
              </div>
          </template>
          <template #footer>
            <div class="flex surface-100 border-round p-2">
              <span class="text-xs font-light">
                Rows: <span class="font-bold">{{ allRosters.length }} / {{ rosterPagination.total_items }}</span>
              </span>
            </div>
          </template>
        </Column>
        <Column field="roster_day" header="Day" class="text-600 w-1">
          <template #body="slotProps">
              <div class=" items-center gap-2">
                    <span class="block pb-1">{{ slotProps.data.roster_day }}</span>
                    <span class="block text-xs text-300">{{ slotProps.data.roster_month }}</span>
              </div>
          </template>
        </Column>
        <Column field="roster_start" sortField="roster_start_unix" header="Timing" sortable class="text-600 w-3">
          <template #body="slotProps">
            <div class="flex gap-2">
                <div class="gap-2 line-height-3">
                      <span class="block flex"><span>{{ slotProps.data.roster_start }}</span></span>
                      <span class="block flex"><span>{{ slotProps.data.roster_end }}</span></span>
                </div>
              <div class="flex surface-100 align-items-center justify-content-center border-round p-2">
                <span class="text-400">{{ calculateDuration(slotProps.data.roster_start_unix, slotProps.data.roster_end_unix) }}</span> 
              </div>
            </div>
          </template>
          <template #footer>
            <div class="flex gap-2">
              <div class="flex surface-100 align-items-center justify-content-center border-round p-2">
                <span class="text-xs font-light">
                  {{ props.viewType === 'leaves' ? 'Leaves' : 'Roster' }}:
                  <span class="font-bold">{{ formatMinutesToTime(totalDuration) }}</span>
                </span>
              </div>
            </div>
          </template>
        </Column>
        <Column header="Status" class="text-center w-1">
          <template #body="slotProps">
            <span v-if="slotProps.data.roster_marked">
            <Tag severity="info" class="block mb-1 surface-300 m-0 p-0" style="letter-spacing: 2px;">MARKED</Tag>
            </span>
            <Tag severity="info" :class="
                                  {'block bg-yellow-900 text-400 m-0 p-0': slotProps.data.shift_type==='LEAVE', 
                                  'block bg-cyan-900 text-400 m-0 p-0': slotProps.data.shift_type==='COVER', 
                                  'block surface-300 m-0 p-0': slotProps.data.shift_type==='SHIFT'}" style="letter-spacing: 2px;">
                                  {{ slotProps.data.shift_type }}
            </Tag>
          </template>
        </Column>
        <Column field="roster_day" header="Comments" class="text-600">
          <template #body="slotProps">
            <div v-if="slotProps.data.roster_logs && slotProps.data.roster_logs.length > 0">
              <div v-for="(log, index) in formatLogs(slotProps.data.roster_logs)" :key="index" class="mb-1">
                <span :class="{
                  'text-yellow-200': log.priority==='MEDIUM', 
                  'text-red-400': log.priority==='HIGH', 
                  'text-xs text-400 mr-2': true}">
                  @{{ log.username }}
                </span>
                <span :class="{
                  'text-yellow-200': log.priority==='MEDIUM', 
                  'text-red-400': log.priority==='HIGH', 
                  'text-xs text-400 mr-2': true}">
                  {{ log.message }}
                </span>
              </div>
            </div>
            <div v-else class="text-xs text-200">No comments</div>
          </template>
        </Column>
        <template #loading>
          <div class="text-400 py-20">Loading Rosters...</div>
        </template>
      </DataTable>
      <Paginator
        class="mt-2"
        :rows="PAGE_SIZE"
        :totalRecords="rosterPagination.total_items"
        :first="first"
        @page="onPage"
      />
    </div>
    <div v-else>
      No {{ props.viewType }} found for this filter.
    </div>
    <Dialog v-model:visible="viewLogs" :style="{ width: '60vw' }" maximizable :modal="true">
      <template #header>
        <h2>Logs</h2>
      </template>
      <LogsDialog :selectedIds="selectedSingleRosterId" logType="roster" @handleBack="handleBack" />
    </Dialog>
</template>

<script setup>
import { ref, onMounted, computed, watch, defineProps, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useRoster } from '@/composables/api/useRoster';
import { useToast } from 'primevue/usetoast';
import { useUtils } from '@/composables/useUtils';
import { useCompanyEmployeeSelection } from '@/composables/useCompanyEmployeeSelection';
import {
  buildDefaultRosterRoute,
  buildRosterRoute,
  createCalendarDate,
  formatCalendarDate,
  getLondonTodayDate,
  isRosterFilteredRoute,
  parseRosterRouteFilters,
  parseRosterSortQuery
} from '@/composables/useRosterRouteFilters';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Dropdown from 'primevue/dropdown';
import Select from 'primevue/select';
import MultiSelect from 'primevue/multiselect';
import RadioButton from 'primevue/radiobutton';
import Paginator from 'primevue/paginator';
import Dialog from 'primevue/dialog';
import Menu from 'primevue/menu';
import defaultUserImage from '@/assets/default-user.png';
import LogsDialog from '@/components/dialogs/LogsDialog.vue';
import Calendar from 'primevue/calendar';

// Props
const props = defineProps({
  targetEmployeeId: {
    type: Number,
    default: 0,
  },
  targetCompanyId: {
    type: Number,
    default: 0,
  },
  targetDateList: {
    type: String,
  },
  viewType: {
    type: String,
    default: 'roster',
    validator: (value) => ['roster', 'leaves'].includes(value)
  }
});

const SCHEDULE_TIMEZONE = 'Europe/London';
const PAGE_SIZE = 100;
const MAX_EDIT_ROSTER_IDS = 100;
const route = useRoute();
const router = useRouter();

// Api: All Rosters 
const {
  allRosters,
  rosterLoading,
  rosterErrors,
  rosterPagination,
  totalDurationMinutes,
  fetchRostersByEmployees
} = useRoster(props.viewType);

// Utils: Calculate Duration
const { calculateDuration } = useUtils();

const {
  companiesList,
  eligibleEmployees,
  employeeOptions,
  selectedCompanyId,
  selectedEmployeeIds: companyEmployeeIds,
  selectedEmployeeFirstNames: companyEmployeeFirstNames,
  selectionLoading,
  handleCompanyChange,
  setRouteSelection,
  ensureSelectionData
} = useCompanyEmployeeSelection();

// Toast: Display Messages
const toast = useToast();

// Refs
const viewLogs = ref(false);
const selectedRows = ref([]);
const currentPage = ref(1);
const sortField = ref('roster_start_unix');
const sortOrder = ref(1);
const menu = ref();
const isRefreshing = ref(false);
const dateRange = ref(null);
const appliedCompanyId = ref(null);
const appliedEmployeeIds = ref([]);
const routeFiltersInitialized = ref(false);
const refreshQueued = ref(false);

const routeFiltersEnabled = computed(() => (
  !props.targetEmployeeId
  && !props.targetCompanyId
  && isRosterFilteredRoute(route.name)
));

const first = computed(() => (currentPage.value - 1) * PAGE_SIZE);

const getSortValues = () => ({
  sort: 'roster',
  order: sortOrder.value === -1 ? 'desc' : 'asc',
  orderCode: sortOrder.value === -1 ? 'DESC' : 'ASC'
});

// Logs: Extract Username
const extractUsername = (email) => {
  return email.split('@')[0];
};

// Computed: Check if Dropdown is Disabled
const isDropdownDisabled = computed(() => {
  return !!props.targetCompanyId && props.targetCompanyId !== 0;
});

// Computed: Format Logs
const formatLogs = computed(() => (logs) => {
  return logs.map(log => ({
    ...log,
    username: extractUsername(log.user)
  }));
});

// Computed: Menu Items
const menuItems = computed(() => {
  const baseItems = [
    {
      label: 'Select Type',
      items: [
        {
          label: 'Shift',
          command: () => {
            createRows(1, 'SHIFT');
          }
        }
      ]
    }
  ];

  if (selectedEmployeeCompanyId.value === 1) {
    baseItems[0].items.push({
      label: 'Cover',
      command: () => {
        createRows(1, 'COVER');
      }
    });
  }

  return baseItems;
});

// Menu: Toggle Menu
const toggle = (event) => {
    menu.value.toggle(event);
};

// Function: Create Rows
const createRows = (_times, type) => {
  if (!selectedEmployeeId.value) {
    return;
  }

  let returnTo;
  if (routeFiltersEnabled.value) {
    const { companyId, startDate, endDate } = getDateRangeParams();
    if (startDate && endDate) {
      returnTo = router.resolve(buildRosterRoute({
        viewType: 'roster',
        companyId,
        employeeIds: [selectedEmployeeId.value],
        filterType: selectedType.value.code,
        markedFilter: selectedMarkedStatus.value,
        startDate,
        endDate,
        page: 1,
        sort: getSortValues().sort,
        order: getSortValues().order
      })).fullPath;
    }
  }

  router.push({
    name: 'CreateRoster',
    params: {
      employeeId: selectedEmployeeId.value,
      shiftType: type.toLowerCase()
    },
    query: returnTo ? { returnTo } : undefined
  });
};

const effectiveEmployeeIds = computed(() => {
  if (props.targetEmployeeId) {
    return [props.targetEmployeeId];
  }

  if (props.targetCompanyId) {
    return eligibleEmployees.value
      .filter(employee => employee.employee_company_id === props.targetCompanyId)
      .map(employee => employee.employee_id);
  }

  return companyEmployeeIds.value;
});

const selectedEmployee = computed(() => {
  if (effectiveEmployeeIds.value.length !== 1) {
    return null;
  }

  return eligibleEmployees.value.find(
    employee => employee.employee_id === effectiveEmployeeIds.value[0]
  ) || null;
});

const employeeSelectionLabel = computed(() => {
  if (companyEmployeeIds.value.length === 0) return 'All Employees';
  if (companyEmployeeIds.value.length === 1) {
    return selectedEmployee.value?.employee_name || '1 Employee selected';
  }
  return companyEmployeeFirstNames.value.join(', ');
});

// Creation is available only when the tree resolves to exactly one employee.
const selectedEmployeeId = computed(() => selectedEmployee.value?.employee_id || null);
const selectedEmployeeCompanyId = computed(() => selectedEmployee.value?.employee_company_id || null);

// Computed: Selected Single Roster ID
const selectedSingleRosterId = computed(() => {
  if (Array.isArray(selectedRows.value)) {
    if (selectedRows.value.length === 1) {
      // Return the roster_id if it exists, otherwise null
      return selectedRows.value[0]?.roster_id ?? null;
    } else if (selectedRows.value.length > 1) {
      return null;
    }
  }
  return null;
});

// Computed: Selected Roster IDs
const selectedRosterIds = computed(() => {
  if (Array.isArray(selectedRows.value) && selectedRows.value.length > 0) {
    return selectedRows.value.map(row => row.roster_id).filter(id => id != null);
  }
  return [];
});

const canMarkSelectedRoster = computed(() => {
  if (props.viewType !== 'roster' || selectedRows.value.length !== 1) {
    return false;
  }

  const selectedRoster = selectedRows.value[0];
  return selectedRoster?.roster_marked === false
    && ['SHIFT', 'COVER'].includes(selectedRoster?.shift_type);
});

const canEditSelectedAttendance = computed(() => {
  if (props.viewType !== 'roster' || selectedRows.value.length !== 1) {
    return false;
  }

  const selectedRoster = selectedRows.value[0];
  return selectedRoster?.roster_marked === true
    && Number.isSafeInteger(Number(selectedRoster?.attendance_id))
    && Number(selectedRoster.attendance_id) > 0;
});

const openAttendanceMarker = () => {
  if (!canMarkSelectedRoster.value) return;

  router.push({
    name: 'MarkAttendance',
    params: { rosterId: selectedRows.value[0].roster_id },
    query: routeFiltersEnabled.value ? { returnTo: route.fullPath } : undefined
  });
};

const openAttendanceEditor = () => {
  if (!canEditSelectedAttendance.value) return;

  const selectedRoster = selectedRows.value[0];
  router.push({
    name: 'EditAttendance',
    params: {
      employeeId: selectedRoster.employee_id,
      rosterId: selectedRoster.roster_id,
      attendanceId: selectedRoster.attendance_id
    },
    query: routeFiltersEnabled.value ? { returnTo: route.fullPath } : undefined
  });
};

// Function: Open the dedicated roster editor.
const openRosterEditor = () => {
  if (selectedRosterIds.value.length === 0) {
    return;
  }

  if (selectedRosterIds.value.length > MAX_EDIT_ROSTER_IDS) {
    toast.add({
      severity: 'warn',
      summary: 'Too many roster records',
      detail: `Select a maximum of ${MAX_EDIT_ROSTER_IDS} roster records to edit at once.`,
      life: 6000,
      group: 'br'
    });
    return;
  }

  router.push({
    name: 'EditRoster',
    params: { rosterIds: selectedRosterIds.value.join(',') },
    query: routeFiltersEnabled.value ? { returnTo: route.fullPath } : undefined
  });
};

// Computed: Type List
const typeList = [
  { name: 'All', code: 'ALL' },
  { name: 'Shift', code: 'SHIFT' },
  { name: 'Cover', code: 'COVER' }
];
const selectedType = ref(typeList[0]);
const markedStatusList = [
  { name: 'All', code: 'ALL' },
  { name: 'Marked', code: 'MARKED' },
  { name: 'Not Marked', code: 'NOTMARKED' }
];
const selectedMarkedStatus = ref('ALL');

// Function: Get Default Date
const getDefaultDate = () => {
  const calendarDate = createCalendarDate(getLondonTodayDate());
  return [calendarDate, new Date(calendarDate)];
};

// Function: Get Date Range Params
const getDateRangeParams = () => {
  const params = {
    companyId: selectedCompanyId.value,
    employeeIds: [...effectiveEmployeeIds.value],
    startDate: null,
    endDate: null
  };

  if (dateRange.value && dateRange.value[0] && dateRange.value[1]) {
    params.startDate = formatCalendarDate(dateRange.value[0]);
    params.endDate = formatCalendarDate(dateRange.value[1]);
  }

  return params;
};

const hasCompleteDateRange = computed(() => (
  Array.isArray(dateRange.value) && dateRange.value[0] && dateRange.value[1]
));

// Function: Handle Date Select
const handleDateSelect = () => {
  currentPage.value = 1;
};

const handleApplyClick = async () => {
  if (!routeFiltersEnabled.value) {
    currentPage.value = 1;
    await refreshData();
    return;
  }

  const { companyId, employeeIds, startDate, endDate } = getDateRangeParams();
  if (!startDate || !endDate) {
    handleError('Please select a complete date range.', 'Applying Roster Filters');
    return;
  }

  const targetRoute = buildRosterRoute({
    viewType: props.viewType,
    companyId,
    employeeIds,
    filterType: selectedType.value.code,
    markedFilter: selectedMarkedStatus.value,
    startDate,
    endDate,
    page: 1,
    sort: getSortValues().sort,
    order: getSortValues().order
  });
  const targetValidation = parseRosterRouteFilters(targetRoute.params, props.viewType);
  if (!targetValidation.valid) {
    handleError(targetValidation.error, 'Applying Roster Filters');
    return;
  }

  const targetFullPath = router.resolve(targetRoute).fullPath;

  if (targetFullPath === route.fullPath) {
    await refreshData();
  } else {
    await router.push(targetRoute);
  }
};

// Function: Refresh Data
const refreshData = async () => {
  if (isRefreshing.value) {
    refreshQueued.value = true;
    return;
  }

  const { companyId, employeeIds, startDate, endDate } = getDateRangeParams();
  if (!startDate || !endDate) {
    handleError('Please select a complete date range.', 'Fetching Roster');
    return;
  }

  isRefreshing.value = true;
  try {
    selectedRows.value = [];
    const requestSucceeded = await fetchRostersByEmployees(
      employeeIds,
      startDate,
      endDate,
      {
        companyId,
        timezone: SCHEDULE_TIMEZONE,
        shiftType: props.viewType === 'leaves' ? 'LEAVE' : selectedType.value.code,
        markedStatus: props.viewType === 'roster' ? selectedMarkedStatus.value : 'ALL',
        page: currentPage.value,
        pageSize: PAGE_SIZE,
        sortOrder: getSortValues().orderCode
      }
    );
    if (requestSucceeded) {
      appliedCompanyId.value = companyId;
      appliedEmployeeIds.value = [...employeeIds];
    }
  } catch (err) {
    handleError(err, 'Fetching Roster');
  } finally {
    isRefreshing.value = false;
    if (refreshQueued.value) {
      refreshQueued.value = false;
      await refreshData();
    }
  }
};

const synchronizeRouteFilters = async () => {
  if (!routeFiltersEnabled.value) {
    return;
  }

  const parsedFilters = parseRosterRouteFilters(route.params, props.viewType);
  const parsedSort = parseRosterSortQuery(route.query);
  if (!parsedFilters.valid || !parsedSort.valid) {
    handleError(parsedFilters.error || parsedSort.error, 'Roster URL');
    await router.replace(buildDefaultRosterRoute(props.viewType));
    return synchronizeRouteFilters();
  }

  const canonicalRoute = buildRosterRoute({
    viewType: props.viewType,
    companyId: parsedFilters.companyId,
    employeeIds: parsedFilters.employeeIds,
    filterType: parsedFilters.filterType,
    markedFilter: parsedFilters.markedFilter,
    startDate: parsedFilters.startDate,
    endDate: parsedFilters.endDate,
    page: parsedFilters.page,
    sort: parsedSort.sort,
    order: parsedSort.order
  });
  if (router.resolve(canonicalRoute).fullPath !== route.fullPath) {
    await router.replace(canonicalRoute);
    return synchronizeRouteFilters();
  }

  const selectionResult = setRouteSelection(
    parsedFilters.companyId,
    parsedFilters.employeeIds
  );
  if (selectionResult.companyMissing || selectionResult.missingEmployeeIds.length > 0) {
    const selectionError = selectionResult.companyMissing
      ? `Company ID is unavailable: ${parsedFilters.companyId}.`
      : `Employee IDs are unavailable for this company: ${selectionResult.missingEmployeeIds.join(', ')}.`;
    handleError(
      selectionError,
      'Roster URL'
    );
    await router.replace(buildDefaultRosterRoute(props.viewType));
    return synchronizeRouteFilters();
  }

  selectedType.value = typeList.find(type => type.code === parsedFilters.typeCode)
    || typeList[0];
  selectedMarkedStatus.value = parsedFilters.markedCode;
  currentPage.value = parsedFilters.page;
  sortOrder.value = parsedSort.sortOrder;
  dateRange.value = [
    parsedFilters.startCalendarDate,
    parsedFilters.endCalendarDate
  ];
  await refreshData();

  if (!rosterErrors.value && currentPage.value > rosterPagination.value.total_pages) {
    await router.replace(buildRosterRoute({
      viewType: props.viewType,
      companyId: parsedFilters.companyId,
      employeeIds: parsedFilters.employeeIds,
      filterType: parsedFilters.filterType,
      markedFilter: parsedFilters.markedFilter,
      startDate: parsedFilters.startDate,
      endDate: parsedFilters.endDate,
      page: rosterPagination.value.total_pages,
      sort: parsedSort.sort,
      order: parsedSort.order
    }));
    return synchronizeRouteFilters();
  }
};

// Watch: Refresh Data on Prop Change
watch(
  [() => props.targetEmployeeId, () => props.targetCompanyId],
  () => {
    if (!routeFiltersEnabled.value) {
      currentPage.value = 1;
      refreshData();
    }
  }
);

watch(() => route.fullPath, () => {
  if (routeFiltersInitialized.value && routeFiltersEnabled.value) {
    synchronizeRouteFilters();
  }
});

// Function: Handle Error
const handleError = (error, context) => {
  console.error(`Error in ${context}:`, error);
  
  let errorMessage = 'An unexpected error occurred.';
  if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  }

  toast.add({
    severity: 'error',
    summary: `Error: ${context}`,
    detail: errorMessage,
    life: 12000,
    group: 'br'
  });
};

// Function: Handle Back
const handleBack = () => {
  viewLogs.value = false;
  selectedRows.value = [];
};

// Function: Handle Page Change
const scrollMainContentToTop = async () => {
  await nextTick();
  const scrollContainer = document.querySelector('[data-main-scroll-container]');

  if (scrollContainer) {
    scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

const onPage = async (event) => {
  const targetPage = event.page + 1;
  selectedRows.value = [];

  if (!routeFiltersEnabled.value) {
    currentPage.value = targetPage;
    await refreshData();
    await scrollMainContentToTop();
    return;
  }

  const parsedFilters = parseRosterRouteFilters(route.params, props.viewType);
  const parsedSort = parseRosterSortQuery(route.query);
  if (!parsedFilters.valid || !parsedSort.valid) {
    handleError(parsedFilters.error || parsedSort.error, 'Roster URL');
    return;
  }

  const targetRoute = buildRosterRoute({
    viewType: props.viewType,
    companyId: parsedFilters.companyId,
    employeeIds: parsedFilters.employeeIds,
    filterType: parsedFilters.filterType,
    markedFilter: parsedFilters.markedFilter,
    startDate: parsedFilters.startDate,
    endDate: parsedFilters.endDate,
    page: targetPage,
    sort: parsedSort.sort,
    order: parsedSort.order
  });

  if (router.resolve(targetRoute).fullPath === route.fullPath) {
    await refreshData();
  } else {
    await router.push(targetRoute);
  }

  await scrollMainContentToTop();
};

const onSort = async event => {
  sortField.value = 'roster_start_unix';
  sortOrder.value = event.sortOrder === -1 ? -1 : 1;

  if (!routeFiltersEnabled.value) {
    currentPage.value = 1;
    await refreshData();
    return;
  }

  const parsedFilters = parseRosterRouteFilters(route.params, props.viewType);
  if (!parsedFilters.valid) {
    handleError(parsedFilters.error, 'Roster URL');
    return;
  }

  const sortValues = getSortValues();
  await router.push(buildRosterRoute({
    viewType: props.viewType,
    companyId: parsedFilters.companyId,
    employeeIds: parsedFilters.employeeIds,
    filterType: parsedFilters.filterType,
    markedFilter: parsedFilters.markedFilter,
    startDate: parsedFilters.startDate,
    endDate: parsedFilters.endDate,
    page: 1,
    sort: sortValues.sort,
    order: sortValues.order
  }));
};

// Watch: Refresh Data on Target Date List Change
watch(() => props.targetDateList, (newValue) => {
  if (newValue && !routeFiltersEnabled.value) {
    dateRange.value = getDefaultDate();
    currentPage.value = 1;
    refreshData();
  }
});

// Watch: Handle Roster Errors
watch(rosterErrors, (newRosterError) => {
  if (newRosterError) {
    handleError(newRosterError, 'Roster');
  }
});

// Initialization: Fetch Data on Component Mount
onMounted(async () => {
  try {
    await ensureSelectionData();
    if (routeFiltersEnabled.value) {
      await synchronizeRouteFilters();
      routeFiltersInitialized.value = true;
    } else {
      dateRange.value = getDefaultDate();
      await refreshData();
    }
  } catch (error) {
    handleError(error, 'Component Initialization');
  }
});

// Add computed property for page title
const pageTitle = computed(() => {
  let selectionLabel = 'All';

  if (appliedEmployeeIds.value.length === 1) {
    selectionLabel = eligibleEmployees.value.find(
      employee => employee.employee_id === appliedEmployeeIds.value[0]
    )?.employee_name || '1 Employee';
  } else if (appliedEmployeeIds.value.length > 1) {
    selectionLabel = `${appliedEmployeeIds.value.length} Employees`;
  } else if (appliedCompanyId.value !== null) {
    selectionLabel = companiesList.value.find(
      company => company.company_id === appliedCompanyId.value
    )?.company_name || 'Company';
  }

  return `${selectionLabel} ${props.viewType === 'leaves' ? 'Leaves' : 'Roster'}`;
});

// Add these computed properties after the other computed properties
const totalDuration = computed(() => totalDurationMinutes.value);

const selectedDurationMinutes = computed(() => {
  const totalMilliseconds = selectedRows.value.reduce((total, roster) => {
    const start = Number(roster.roster_start_unix);
    const end = Number(roster.roster_end_unix);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return total;
    return total + (end - start);
  }, 0);
  return Math.floor(totalMilliseconds / 60000);
});

const selectionSummary = computed(() => {
  const rowLabel = selectedRows.value.length === 1 ? 'Row' : 'Rows';
  const durationLabel = props.viewType === 'leaves' ? 'Leave' : 'Roster';
  return `${selectedRows.value.length} ${rowLabel} Selected with ${formatMinutesToTime(selectedDurationMinutes.value)} Total ${durationLabel} Hours.`;
});

// Helper function to convert minutes to HH:mm format
const formatMinutesToTime = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};
</script>

<style scoped>
.roster-sticky-header {
  position: sticky;
  top: 0;
  z-index: 20;
  display: grid;
  grid-template-columns: minmax(0, 35%) minmax(0, 65%);
  align-items: center;
  padding-block: 0.5rem;
  box-shadow: 0 1px 0 var(--p-content-border-color);
}

.roster-header-summary,
.roster-filters {
  min-width: 0;
}

.leaves-sticky-header {
  grid-template-columns: minmax(0, 25%) minmax(0, 75%);
}

.roster-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.marked-filter {
  min-height: 2.25rem;
  color: var(--p-text-muted-color);
  font-size: 0.875rem;
}

@media (max-width: 991px) {
  .roster-sticky-header {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.5rem;
  }

  .roster-filters {
    justify-content: flex-start;
  }
}
</style>
